import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, tap } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { AffairOptimizationRunResponse } from '../../interventions/services/interventions.service'

export interface OptimizationRunSummary {
  id: number
  status: string
  dateFrom: string | null
  dateTo: string | null
  serviceDuration: number | null
  maxRadiusKm: number | null
  clustersCount: number | null
  techniciansCount: number | null
  affairsCount: number | null
  affairsWithSlotsCount: number | null
  errorMessage: string | null
  summary: any
  durationSec: number | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
}

interface RunsListResponse {
  data: OptimizationRunSummary[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

@Injectable({
  providedIn: 'root',
})
export class OptimizationDebugService {
  private http = inject(HttpClient)
  private apiUrl = environment.planifyApiUrl

  private runs = new BehaviorSubject<OptimizationRunSummary[]>([])
  private total = new BehaviorSubject<number>(0)
  private loading = new BehaviorSubject<boolean>(false)
  private detailLoading = new BehaviorSubject<boolean>(false)
  private actionLoading = new BehaviorSubject<boolean>(false)

  public runs$ = this.runs.asObservable()
  public total$ = this.total.asObservable()
  public loading$ = this.loading.asObservable()
  public detailLoading$ = this.detailLoading.asObservable()
  public actionLoading$ = this.actionLoading.asObservable()

  getRuns(page = 1, perPage = 20) {
    this.loading.next(true)
    const params = new HttpParams().set('page', page).set('perPage', perPage)
    return this.http
      .get<RunsListResponse>(`${this.apiUrl}/affairs/optimization/runs`, {
        params,
      })
      .pipe(
        tap((res) => {
          this.runs.next(res.data)
          this.total.next(res.meta.total)
          this.loading.next(false)
        })
      )
  }

  syncPool() {
    this.actionLoading.next(true)
    return this.http
      .post<{
        status: string
        message: string
      }>(`${this.apiUrl}/affairs/optimization/sync-pool`, {})
      .pipe(tap({ complete: () => this.actionLoading.next(false) }))
  }

  launchOptimization(async = false) {
    this.actionLoading.next(true)
    return this.http
      .post<AffairOptimizationRunResponse>(
        `${this.apiUrl}/affairs/optimization/launch`,
        { async }
      )
      .pipe(tap({ complete: () => this.actionLoading.next(false) }))
  }

  cancelRun(runId: number) {
    this.actionLoading.next(true)
    return this.http
      .post<AffairOptimizationRunResponse>(
        `${this.apiUrl}/affairs/optimization/runs/${runId}/cancel`,
        {}
      )
      .pipe(tap({ complete: () => this.actionLoading.next(false) }))
  }

  getRunDetail(runId: number) {
    this.detailLoading.next(true)
    return this.http
      .get<AffairOptimizationRunResponse>(
        `${this.apiUrl}/affairs/optimization/runs/${runId}`
      )
      .pipe(tap(() => this.detailLoading.next(false)))
  }
}
